import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DateTime, Icon, InfiniteScrollingObserver } from '~/components';
import { useApi } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { Footer, Header } from '../components';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import ReactMarkdown from 'react-markdown';

const Loading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
`;

const PageContent = styled.div`
  flex: 1;
  width: 49.5rem;
  max-width: 90%;
  margin: 2rem auto;
`;

const Item = styled.div`
  flex: 1;
  padding-top: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${colors.grey10};
  scroll-margin-top: 6rem;

  &:last-child {
    border-bottom: none;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: ${weights.black};
  text-align: left;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: ${weights.medium};
  text-align: left;
  padding-top: 2px;
  padding-bottom: 2px;
`;

const Content = styled.div`
  font-size: 1rem;
  font-weight: ${weights.light};
  text-align: justify;
  padding-top: 2px;
  padding-bottom: 2px;
  white-space: pre-wrap;

  ul {
    margin-top: -0.5rem;
    padding-left: 1.5rem;
    list-style-type: disc;
    line-height: 1rem;
  }

  li {
    margin-bottom: 0.25rem;
    line-height: 1.5rem;
  }
`;

const Image = styled.img`
  display: block;
  margin: 2rem auto;
  border-radius: 6px;
  box-shadow: 0px 3px 45px 0px ${colors.grey10};
`;

const LoaderCard = styled.div`
  opacity: 0.5;
`;

export default function WhatsNewPage() {
  useDocumentTitle("What's New");
  const api = useApi();

  const [query, setQuery] = useState({
    data: null,
    params: {
      page: 0,
      size: 25,
    },
    action: 'load',
    status: 'loading',
  });

  const { status, data, params } = query;

  const urlSearchParams = useMemo(
    () => ({
      page: params.page,
      size: params.size,
    }),
    [params],
  );

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.productUpdates().get(urlSearchParams);

      setQuery((state) => ({
        ...state,
        action: null,
        dialog: null,
        status: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch {
      // Do nothing
    }
  }, [api, urlSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.status !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  }, []);

  function preserveBlankLines(text) {
    return text.replace(/(?<=\n\n)(?![*-])\n/gi, '&nbsp;\n ');
  }

  return (
    <>
      <Header />
      {status === 'loading' || !data ? (
        <Loading>
          <Icon icon="spinner" spin={true} />
        </Loading>
      ) : (
        <PageContent>
          <PageTitle>What's New in Ruddr</PageTitle>
          {data.results.map((item) => {
            return (
              <Item key={item.id}>
                <DateTime
                  value={item.date}
                  format={dateFormats.longDate}
                  style={{ color: colors.grey40, fontSize: '0.825rem' }}
                />
                <Title>{item.title}</Title>
                <Content>
                  <ReactMarkdown>{preserveBlankLines(item.content)}</ReactMarkdown>
                </Content>
                {item.imageUrl && (
                  <Image
                    src={item.imageUrl}
                    alt={item.title}
                    width={item.imageWidthPercent != null ? `${item.imageWidthPercent}%` : undefined}
                  />
                )}
              </Item>
            );
          })}

          {data.total > data.results.length && (
            <LoaderCard>
              <InfiniteScrollingObserver key={query.data.results.length} onIntersecting={loadMore} />
            </LoaderCard>
          )}
        </PageContent>
      )}
      <Footer />
    </>
  );
}
